import React, { useState, useRef, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { Edit2, Check, X } from 'lucide-react';
import type { IRiskInputModel } from 'inputModels';

interface RiskNameEditorProps {
  risk: IRiskInputModel;
  riskIndex: number;
  onNameChange: (index: number, name: string) => void;
  onRemove: (index: number) => void;
}

const RiskNameEditor: React.FC<RiskNameEditorProps> = ({
  risk,
  riskIndex,
  onNameChange,
  onRemove,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(risk.name);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [isEditing]);

  const saveName = () => {
    const trimmedName = editingName.trim();
    if (trimmedName) {
      onNameChange(riskIndex, trimmedName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveName();
    }
    if (e.key === 'Escape') {
      setEditingName(risk.name);
      setIsEditing(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isEditing ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <input
            ref={editInputRef}
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={saveName}
            onKeyDown={handleKeyDown}
            style={{
              width: '120px',
              border: 'none',
              borderBottom: '1px solid #ccc',
              padding: '2px 4px',
              fontSize: 'inherit',
              backgroundColor: 'transparent',
              outline: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              saveName();
            }}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              ml: 0.5,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <Check size={14} />
          </Button>
        </Box>
      ) : (
        <>
          <span>{risk.name || `Risco ${riskIndex + 1}`}</span>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
              setEditingName(risk.name);
            }}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              ml: 0.5,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <Edit2 size={14} />
          </Button>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(riskIndex);
            }}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <X size={14} />
          </Button>
        </>
      )}
    </Box>
  );
};

export default RiskNameEditor;
